import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { getLevelInfo } from '../utils/xpUtils';
import { Link } from 'react-router-dom';
import useGameChallenge from '../hooks/useGameChallenge';
import ChallengeInviteModal from '../components/ChallengeInviteModal';
import ChallengeDuelBanner from '../components/ChallengeDuelBanner';
import RivalryCard from '../components/RivalryCard';
import Icon from '../components/Icon';

const TIERS = [
  { tier: 1, target: 1000, xp: 100,  label: '< 1.0s', color: '#a3e635' },
  { tier: 2, target: 800,  xp: 150,  label: '< 0.8s', color: '#84cc16' },
  { tier: 3, target: 600,  xp: 200,  label: '< 0.6s', color: '#facc15' },
  { tier: 4, target: 500,  xp: 300,  label: '< 0.5s', color: '#fb923c' },
  { tier: 5, target: 400,  xp: 400,  label: '< 0.4s', color: '#f97316' },
  { tier: 6, target: 300,  xp: 1000, label: '< 0.3s', color: '#ef4444' },
  { tier: 7, target: 200,  xp: 2000, label: '< 0.2s', color: '#e10600' },
];

const STATES = { IDLE: 'idle', LIGHTING: 'lighting', WAITING: 'waiting', GO: 'go', RESULT: 'result', FALSE_START: 'false_start' };

const Light = ({ on }) => {
  const offCls = 'w-10 h-10 sm:w-14 sm:h-14 rounded-full border-4 transition-all duration-150 bg-zinc-900 border-zinc-700 shadow-none';
  const onCls  = 'w-10 h-10 sm:w-14 sm:h-14 rounded-full border-4 transition-all duration-150 bg-red-600 border-red-400 shadow-[0_0_24px_8px_rgba(220,38,38,0.7)]';
  return (
    <div className="flex flex-col items-center gap-2">
      <div className={offCls} />
      <div className={on ? onCls : offCls} />
    </div>
  );
};

export default function LightSpeed() {
  const { user, refreshUser } = useAuth();
  const [gameState, setGameState] = useState(STATES.IDLE);
  const [litCount, setLitCount] = useState(0);
  const [reactionTime, setReactionTime] = useState(null);
  const [result, setResult] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [myBest, setMyBest] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [userXP, setUserXP] = useState(user?.xp ?? 0);
  const [showChallengeModal, setShowChallengeModal] = useState(false);

  const { activeChallenge, opponent, myScore, opponentScore, hasSubmitted, isCompleted, iWon, submitScore, submitting: challengeSubmitting, refresh: refreshChallenge } = useGameChallenge('lightspeed');

  const goTimeRef = useRef(null);
  const lightTimerRef = useRef(null);
  const waitTimerRef = useRef(null);

  const fetchLeaderboard = useCallback(() => {
    api.get('/game/lightspeed/leaderboard').then(r => setLeaderboard(r.data.leaderboard || [])).catch(() => {});
  }, []);

  const fetchMyBest = useCallback(() => {
    if (!user) return;
    api.get('/game/lightspeed/my-best').then(r => setMyBest(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => { fetchLeaderboard(); fetchMyBest(); }, [fetchLeaderboard, fetchMyBest]);

  const cleanup = () => {
    clearTimeout(lightTimerRef.current);
    clearTimeout(waitTimerRef.current);
  };

  const playLightSound = () => {
    const a = new Audio('/feu.wav');
    a.play().catch(() => {});
  };

  const startGame = () => {
    cleanup();
    setLitCount(0);
    setReactionTime(null);
    setResult(null);
    setGameState(STATES.LIGHTING);

    let count = 0;
    const lightUp = () => {
      count++;
      setLitCount(count);
      playLightSound();
      if (count < 5) {
        lightTimerRef.current = setTimeout(lightUp, 1000);
      } else {
        setGameState(STATES.WAITING);
        const delay = 500 + Math.random() * 6500;
        waitTimerRef.current = setTimeout(() => {
          setLitCount(0);
          setGameState(STATES.GO);
          goTimeRef.current = performance.now();
        }, delay);
      }
    };
    lightTimerRef.current = setTimeout(lightUp, 800);
  };

  const handleGo = () => {
    if (gameState === STATES.LIGHTING || gameState === STATES.WAITING) {
      cleanup();
      setLitCount(0);
      setGameState(STATES.FALSE_START);
      if (user) {
        api.post('/game/lightspeed/false-start')
          .then(() => refreshUser())
          .catch(() => {});
      }
      return;
    }
    if (gameState !== STATES.GO) return;

    const elapsed = Math.round(performance.now() - goTimeRef.current);
    setReactionTime(elapsed);
    setGameState(STATES.RESULT);

    if (!user) return;
    setSubmitting(true);
    api.post('/game/lightspeed', { reactionTime: elapsed })
      .then(r => {
        setResult(r.data);
        if (r.data.xpEarned > 0) refreshUser();
        fetchLeaderboard();
        fetchMyBest();
      })
      .catch(() => {})
      .finally(() => setSubmitting(false));
  };

  const beatTier = reactionTime !== null ? TIERS.filter(t => reactionTime < t.target).at(-1) : null;

  return (
    <div className="max-w-4xl mx-auto px-4 py-10 select-none">
      {/* Header */}
      <div className="mb-8 text-center">
        <p className="text-zinc-500 text-xs uppercase tracking-widest font-medium mb-1">Mini-jeu</p>
        <h1 className="text-4xl font-black text-white tracking-tight">
          Light<span className="text-f1red">Speed</span>
        </h1>
        <p className="text-zinc-400 mt-2 text-sm max-w-xs mx-auto">
          Attends que les 5 feux s'éteignent, puis appuie sur GO aussi vite que possible.
        </p>
      </div>

      <ChallengeDuelBanner
        activeChallenge={activeChallenge}
        opponent={opponent}
        myScore={myScore}
        opponentScore={opponentScore}
        hasSubmitted={hasSubmitted}
        isCompleted={isCompleted}
        iWon={iWon}
        myLiveScore={gameState === STATES.RESULT ? reactionTime : null}
        canSubmit={gameState === STATES.RESULT && reactionTime !== null && !hasSubmitted}
        formatScore={(s) => s !== null ? `${(s / 1000).toFixed(3)}s` : null}
        onSubmit={() => submitScore(reactionTime)}
        submitting={challengeSubmitting}
        game="lightspeed"
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
        {/* Game area */}
        <div className="md:col-span-2 flex flex-col gap-6">
          {/* Lights panel */}
          <div className="card flex flex-col items-center gap-8 py-10">
            {/* F1 light gantry */}
            <div className="flex items-center gap-4 sm:gap-6 bg-zinc-950 rounded-2xl px-8 py-6 border border-zinc-800">
              {[1,2,3,4,5].map(i => (
                <Light key={i} on={
                  gameState === STATES.LIGHTING && i <= litCount ||
                  gameState === STATES.WAITING
                } />
              ))}
            </div>

            {/* Game state message */}
            <div className="text-center min-h-[60px] flex flex-col items-center justify-center gap-2">
              {gameState === STATES.IDLE && (
                <p className="text-zinc-400 text-sm">Prêt ? Appuie sur Start.</p>
              )}
              {gameState === STATES.LIGHTING && (
                <p className="text-zinc-300 text-sm animate-pulse">Les feux s'allument…</p>
              )}
              {gameState === STATES.WAITING && (
                <p className="text-zinc-300 text-sm animate-pulse font-medium">Attention…</p>
              )}
              {gameState === STATES.GO && (
                <p className="text-2xl font-black text-green-400 animate-bounce">GO !</p>
              )}
              {gameState === STATES.FALSE_START && (
                <div className="text-center">
                  <p className="text-2xl font-black text-red-500">Faux départ !</p>
                  <p className="text-zinc-400 text-sm mt-1">Tu as appuyé trop tôt.</p>
                  {user && <p className="text-red-400 text-xs font-bold mt-1">− 1 000 XP</p>}
                </div>
              )}
              {gameState === STATES.RESULT && reactionTime !== null && (
                <div className="text-center">
                  <p className="text-5xl font-black text-white">{(reactionTime / 1000).toFixed(3)}<span className="text-xl text-zinc-400 ml-1">s</span></p>
                  {beatTier ? (
                    <div className="mt-2 flex flex-col items-center gap-1">
                      <p className="text-sm font-bold" style={{ color: beatTier.color }}>
                        Tier {beatTier.tier} débloqué — {beatTier.label}
                      </p>
                      {result && (
                        <span className="text-xs font-black text-yellow-400 bg-yellow-900/20 px-2 py-0.5 rounded-full">
                          +{result.xpEarned} XP
                        </span>
                      )}
                    </div>
                  ) : (
                    <p className="text-zinc-500 text-sm mt-1">Essaie de passer sous la seconde !</p>
                  )}
                  {result?.personalBest && (
                    <p className="text-zinc-400 text-xs mt-1">Moyenne : <span className="text-white font-bold">{(result.personalBest / 1000).toFixed(3)}s</span></p>
                  )}
                </div>
              )}
            </div>

            {/* Buttons */}
            <div className="flex flex-col items-center gap-3 w-full">
              <div className="flex items-center gap-4">
                {(gameState === STATES.IDLE || gameState === STATES.RESULT || gameState === STATES.FALSE_START) && (
                  <button onClick={startGame}
                    className="px-8 py-3 bg-zinc-800 hover:bg-zinc-700 text-white font-bold rounded-xl border border-zinc-700 transition-all text-sm">
                    {gameState === STATES.IDLE ? 'Start' : 'Rejouer'}
                  </button>
                )}
                {(gameState === STATES.LIGHTING || gameState === STATES.WAITING || gameState === STATES.GO) && (
                  <button onClick={handleGo}
                    className={`px-12 py-4 font-black text-xl rounded-xl border-4 transition-all ${
                      gameState === STATES.GO
                        ? 'bg-f1red border-red-400 text-white shadow-[0_0_30px_rgba(225,6,0,0.5)] scale-105 hover:scale-110'
                        : 'bg-zinc-900 border-zinc-700 text-zinc-500 cursor-pointer'
                    }`}>
                    GO
                  </button>
                )}
              </div>

              {/* Affronter button */}
              {user && (gameState === STATES.IDLE || gameState === STATES.RESULT || gameState === STATES.FALSE_START) && (
                <button onClick={() => setShowChallengeModal(true)}
                  className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-bold border border-zinc-700 text-zinc-300 hover:border-f1red/50 hover:text-white transition-all">
                  <Icon name="sword" size={13} className="shrink-0" /> Affronter un ami
                </button>
              )}
            </div>

            {!user && gameState === STATES.RESULT && (
              <p className="text-zinc-500 text-xs text-center">
                <Link to="/login" className="text-f1red underline">Connecte-toi</Link> pour sauvegarder ton score et gagner de l'XP.
              </p>
            )}
          </div>

          {/* Tiers */}
          <div className="card">
            <h2 className="font-bold text-white mb-3 text-sm">Paliers de récompense</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {TIERS.map(t => {
                const beaten = reactionTime !== null && reactionTime < t.target;
                const isPB = myBest && myBest.personalBest < t.target;
                return (
                  <div key={t.tier} className={`rounded-xl px-3 py-2 border text-center transition-all ${
                    beaten ? 'border-opacity-60' : isPB ? 'border-zinc-700 bg-zinc-800/40' : 'border-zinc-800'
                  }`} style={beaten ? { borderColor: t.color, backgroundColor: `${t.color}18` } : {}}>
                    <p className={`text-xs font-black ${!beaten && !isPB ? 'text-zinc-500' : ''}`} style={{ color: beaten || isPB ? t.color : undefined }}>
                      {t.label}
                    </p>
                    <p className={`text-[10px] mt-0.5 ${beaten ? 'text-white' : 'text-zinc-600'}`}>+{t.xp} XP</p>
                    {isPB && !beaten && <p className="text-[8px] text-zinc-500 mt-0.5">Ton PB</p>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Leaderboard + Rivalry — right column */}
        <div className="flex flex-col gap-3">
        <div className="card">
          <h2 className="font-bold text-white mb-4 flex items-center gap-2 text-sm">
            <Icon name="podium" size={13} className="shrink-0" /> Classement
          </h2>
          {leaderboard.length === 0 ? (
            <p className="text-zinc-500 text-xs text-center py-4">Aucun score encore.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {leaderboard.map((entry, i) => {
                const beaten = TIERS.filter(t => entry.score < t.target);
                const tierObj = beaten.length > 0 ? beaten[beaten.length - 1] : null;
                const isMe = user && entry.user?.username === user.username;
                return (
                  <div key={i} className={`flex items-center gap-2 px-2.5 py-2 rounded-xl border ${
                    isMe ? 'border-f1red/40 bg-f1red/5' : 'border-zinc-800'
                  }`}>
                    <span className={`text-xs font-black w-5 text-center ${
                      i === 0 ? 'text-yellow-400' : i === 1 ? 'text-zinc-300' : i === 2 ? 'text-amber-600' : 'text-zinc-600'
                    }`}>
                      {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i+1}`}
                    </span>
                    {entry.user?.avatar
                      ? <img src={entry.user.avatar} alt="" className="w-6 h-6 rounded-full object-cover shrink-0" />
                      : <div className="w-6 h-6 rounded-full bg-zinc-700 flex items-center justify-center text-[10px] font-black text-zinc-300 shrink-0">
                          {entry.user?.username?.[0]?.toUpperCase()}
                        </div>
                    }
                    <span className={`text-xs font-semibold flex-1 truncate ${isMe ? 'text-f1red' : 'text-zinc-300'}`}>
                      {entry.user?.username}
                    </span>
                    <div className="text-right shrink-0">
                      <p className="text-xs font-black text-white">{(entry.score / 1000).toFixed(3)}s</p>
                      {tierObj && <p className="text-[9px]" style={{ color: tierObj.color }}>T{tierObj.tier}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {myBest?.personalBest && (
            <div className="mt-4 pt-3 border-t border-zinc-800">
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Ta moyenne</p>
              <p className="text-white font-black text-lg">{(myBest.personalBest / 1000).toFixed(3)}<span className="text-zinc-500 text-sm">s</span></p>
              <p className="text-zinc-500 text-[10px]">{myBest.attempts} essai{myBest.attempts > 1 ? 's' : ''}</p>
            </div>
          )}
        </div>
        <RivalryCard opponent={opponent} game="lightspeed" />
        </div>
      </div>
    {showChallengeModal && (
      <ChallengeInviteModal game="lightspeed"
        onClose={() => setShowChallengeModal(false)}
        onSent={() => { setShowChallengeModal(false); refreshChallenge(); }} />
    )}
    </div>
  );
}
